import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PdMark from '../components/PdMark';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

const StepHeading = ({ n, children }) => (
  <h2 className="font-heading text-[20px] font-semibold tracking-[-0.02em] flex items-baseline gap-3">
    <span className="font-heading text-[28px] font-bold tracking-[-0.035em] leading-none text-quiet" aria-hidden="true">{n}</span>
    <span><span className="sr-only">Step {n}: </span>{children}</span>
  </h2>
);

export const SetupMFA = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setupMfa, completeMfaSetup } = useAuth();

  const [mfaData, setMfaData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);

  // Check if user came from profile page OR has already completed onboarding
  // If onboarding is complete, they must have come from profile (or typed URL directly)
  const fromProfile = location.state?.from === 'profile' || user?.onboarding_complete;

  useEffect(() => {
    const initMfa = async () => {
      try {
        const data = await setupMfa();
        setMfaData(data);
      } catch {
        toast.error('We could not start two-factor setup. Try again in a moment.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      initMfa();
    }
  }, [user, setupMfa]);

  const copySecret = () => {
    if (mfaData?.secret) {
      navigator.clipboard.writeText(mfaData.secret);
      setCopied(true);
      toast.success('Secret key copied');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleVerify = async () => {
    if (verifyCode.length !== 6) return;

    setVerifying(true);
    try {
      await completeMfaSetup(verifyCode);
      toast.success('Two-factor authentication is on');
      // Navigate back to profile if came from there, otherwise to onboarding
      navigate(fromProfile ? '/profile' : '/onboarding');
    } catch {
      toast.error('That code is not right. Try the next one your app shows.');
      setVerifyCode('');
    } finally {
      setVerifying(false);
    }
  };

  const skipMfa = () => {
    if (fromProfile) {
      // Just go back to profile without any toast
      navigate('/profile');
    } else {
      toast.info('You can turn this on later from Profile → Security');
      navigate('/onboarding');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ground flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="mb-6 flex items-center gap-3">
          <PdMark size={28} className="text-brand" />
          <span className="font-heading text-[18px] font-bold tracking-[-0.02em] text-ink">Precious Data</span>
        </div>

        <div className="bg-surface rounded-xl p-7 sm:p-9">
          <h1 className="font-heading text-[28px] font-bold tracking-[-0.035em] leading-[1.05]">
            Add a second lock to your account
          </h1>
          <p className="text-ink-70 mt-3">
            With two-factor authentication, signing in needs your password and a six-digit code
            from your phone. Someone who learns your password still cannot get in.
          </p>

          <div className="space-y-8 mt-8">
            {/* Step 1: Install App */}
            <section className="space-y-2">
              <StepHeading n={1}>Install an authenticator app</StepHeading>
              <p className="text-ink-70">
                Google Authenticator, Authy, or any app that makes time-based codes will work.
              </p>
            </section>

            {/* Step 2: Scan QR or Enter Key */}
            <section className="space-y-4">
              <StepHeading n={2}>Add your account to the app</StepHeading>

              {/* QR Code */}
              {mfaData?.qr_code_uri ? (
                <div className="bg-surface p-3 rounded-lg inline-block border border-rule">
                  <QRCodeSVG
                    value={mfaData.qr_code_uri}
                    size={192}
                    level="M"
                    includeMargin={false}
                  />
                </div>
              ) : (
                <div className="w-[216px] h-[216px] bg-inset rounded-lg flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-ink-55" />
                </div>
              )}

              <p className="text-sm text-ink-55">Or type this secret key into the app instead:</p>

              <div className="flex items-center gap-2">
                <code className="flex-1 bg-ground px-4 py-3 rounded-md pd-mono text-ink break-all normal-case tracking-[0.08em]">
                  {mfaData?.secret}
                </code>
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={copySecret}
                  className="flex-shrink-0"
                  aria-label="Copy secret key"
                  data-testid="copy-secret-btn"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-brand" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </section>

            {/* Step 3: Verify */}
            <section className="space-y-4">
              <StepHeading n={3}>Enter the six-digit code it shows</StepHeading>

              <InputOTP
                value={verifyCode}
                onChange={setVerifyCode}
                maxLength={6}
                data-testid="mfa-setup-code-input"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>

              <Button
                onClick={handleVerify}
                disabled={verifyCode.length !== 6 || verifying}
                className="w-full"
                data-testid="enable-mfa-btn"
              >
                {verifying ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Turn on two-factor authentication'
                )}
              </Button>
            </section>

            <div className="border-t border-rule pt-6">
              <Button
                variant="ghost"
                onClick={skipMfa}
                className="w-full"
                data-testid="skip-mfa-btn"
              >
                {fromProfile ? 'Back to profile' : 'Not now — I will do this later'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupMFA;
